import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../providers/AuthContext";
import { checkIfUserIsRole } from "../utils/authUtils";
import { formatDateTime } from "../utils/textUtils";
import avetaLogo from "../assets/aveta-logo.png";
import SettingsModal from "./modals/SettingsModal";
import Banner from "./Banner";

interface HeaderProps {
  title?: string;
  updateAvailable?: boolean;
  onUpdateAction?: () => void;
  scheduledMaintenance?: {
    message?: string;
    startDateTime?: string;
    endDateTime?: string;
  } | null;
  onDismissScheduledMaintenance?: () => void;
}

const Header: React.FC<HeaderProps> = ({
  title = "",
  updateAvailable = false,
  onUpdateAction,
  scheduledMaintenance = null,
  onDismissScheduledMaintenance,
}) => {
  const { user, logout, getToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Check if user has debugger role
  useEffect(() => {
    const checkRole = async () => {
      try {
        const token = await getToken();
        setIsAdmin(checkIfUserIsRole(token, "admin"));
      } catch (error) {
        console.error("Error checking role:", error);
        setIsAdmin(false);
      }
    };

    checkRole();
  }, [getToken]);

  const handleLogout = () => {
    logout();
  };

  const handleDebuggerClick = () => {
    navigate("/debugger");
  };

  const handleHomeClick = () => {
    navigate("/");
  };

  const handleSettingsClick = () => {
    setShowSettings(true);
  };

  const isOnDebuggerPage = location.pathname === "/debugger";

  return (
    <header className="sticky top-0 z-40 shadow-md border-b-2 border-[#46ACC6]/20 backdrop-blur-sm bg-[#e3f2fd] flex-shrink-0">
      <div className="flex flex-col">
        {/* Main Header Content */}
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 bg-transparent h-16">
          <div className="flex items-center h-full bg-transparent">
            <img
              src={avetaLogo}
              alt="Aveta Logo"
              className="mt-2 h-8 w-auto sm:h-10 md:h-12 lg:h-12 mr-2 sm:mr-3 drop-shadow-md"
            />
            <h1 className="text-lg sm:text-xl md:text-2xl lg:text-2xl text-[#46ACC6] font-semibold drop-shadow-md">
              {title}
            </h1>
          </div>

          <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-4 bg-transparent">
            {user && (
              <span className="text-xs sm:text-sm md:text-base text-[#46ACC6] hidden sm:block font-medium drop-shadow-sm">
                Welcome, {user.name || user.username}
              </span>
            )}

            {/* Settings button */}
            <button
              onClick={handleSettingsClick}
              className="p-2 text-[#46ACC6] bg-white/20 hover:bg-white/30 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300 hover:shadow-lg hover:scale-102 backdrop-blur-sm"
              title="Settings"
            >
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </button>

            {/* Navigation buttons */}
            {isAdmin && (
              <>
                {!isOnDebuggerPage && (
                  <button
                    onClick={handleDebuggerClick}
                    className="p-2 text-[#46ACC6] bg-white/20 hover:bg-white/30 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300 hover:shadow-lg hover:scale-102 backdrop-blur-sm"
                    title="Debug"
                  >
                    <div className="flex items-center space-x-2">
                      <svg
                        className="w-4 h-4 sm:w-5 sm:h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <g transform="translate(5 5)">
                          <path
                            fill="currentColor"
                            d="M-1.898.223A3.004 3.004 0 0 0 1.78 3.897l6.341 6.252A3.003 3.003 0 0 0 11 14a3 3 0 1 0-.851-5.878L3.897 1.781A3.004 3.004 0 0 0 .223-1.9l2.141 2.142L2 2l-1.757.364zm13.37 9.019.528.026.287.445.445.287.026.529L13 11l-.242.471-.026.529-.445.287-.287.445-.529.026L11 13l-.471-.242-.529-.026-.287-.445-.445-.287-.026-.529L9 11l.242-.471.026-.529.445-.287.287-.445.529-.026L11 9z"
                          />
                        </g>
                      </svg>
                    </div>
                  </button>
                )}
                {isOnDebuggerPage && (
                  <button
                    onClick={handleHomeClick}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm"
                  >
                    Chat
                  </button>
                )}
              </>
            )}

            <button
              onClick={handleLogout}
              className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm md:text-base text-[#46ACC6] bg-white/20 hover:bg-white/30 border border-[#46ACC6]/40 hover:border-[#46ACC6]/60 rounded-md transition-all duration-300 hover:shadow-lg hover:scale-102 font-medium backdrop-blur-sm"
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Update Banner */}
        {updateAvailable && (
          <Banner
            type="update"
            message="A new version is available. Please reload to get the latest features."
            actionLabel="Update Now"
            onAction={onUpdateAction}
          />
        )}

        {/* Scheduled Maintenance Banner */}
        {scheduledMaintenance && (
          <Banner
            type="maintenance"
            message={
              <>
                {(scheduledMaintenance.message
                  ? scheduledMaintenance.message.replace(/[.,?!]/g, "")
                  : undefined) || "Scheduled maintenance is planned."}
                {scheduledMaintenance.startDateTime && (
                  <>
                    {" "}
                    from{" "}
                    {formatDateTime(scheduledMaintenance.startDateTime, {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      timeZoneName: "short",
                    })}
                  </>
                )}
                {scheduledMaintenance.endDateTime && (
                  <>
                    {" "}
                    until{" "}
                    {formatDateTime(scheduledMaintenance.endDateTime, {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      timeZoneName: "short",
                    })}
                  </>
                )}
                .
              </>
            }
            onDismiss={onDismissScheduledMaintenance}
          />
        )}
      </div>

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </header>
  );
};

export default Header;
